import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Send, Loader2, Maximize2, Minimize2, Paperclip, Building2, Bot, Sparkles, ChevronDown, Mic, Square, Zap, CheckCircle2, Volume2, Moon, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const Md = ({ children }) => (
  <div className="text-[14px] leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:font-bold [&_strong]:text-slate-900 [&_h1]:mb-1.5 [&_h1]:mt-1 [&_h1]:text-[15px] [&_h1]:font-extrabold [&_h2]:mb-1 [&_h2]:mt-1.5 [&_h2]:text-[14px] [&_h2]:font-bold [&_h3]:font-bold [&_a]:text-[#1a5fd0] [&_a]:underline [&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[12px]">
    <ReactMarkdown>{children}</ReactMarkdown>
  </div>
);

export default function CommandCenter() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("default");
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [teamOpen, setTeamOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [full, setFull] = useState(false);
  const [autoPilot, setAutoPilot] = useState(true);
  const [awayMode, setAwayMode] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const endRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/companies").then(({ data }) => {
      setCompanies(data.companies);
      if (data.companies[0]) setCompanyId(data.companies[0].id);
    }).catch(() => {});
    api.get("/employees").then(({ data }) => setEmployees(data.employees)).catch(() => {});
    api.get("/ai/away-mode").then(({ data }) => setAwayMode(data.enabled)).catch(() => {});
    api.get("/ops/operating-mode").then(({ data }) => {
      setAutoPilot((data.autopilot_modes || []).includes(data.settings?.mode));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/ceo/messages?company_id=${companyId}`).then(({ data }) => setMessages(data.messages)).catch(() => setMessages([]));
  }, [companyId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const sendText = async (text) => {
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const { data } = await api.post("/ceo/chat", { message: text, company_id: companyId, employees: selectedEmployees.length ? selectedEmployees : undefined, auto_pilot: autoPilot });
      setMessages((m) => [...m, { role: "controller", text: data.reply, model: data.model, mission_id: data.mission?.id, assignees: data.assignees, deliverables: data.deliverables }]);
      if (data.wrapup) setMessages((m) => [...m, { role: "controller", text: data.wrapup, mission_id: data.mission?.id, wrapup: true }]);
    } catch {
      toast.error("The AI Controller could not respond. Please try again.");
      setMessages((m) => [...m, { role: "controller", text: "I hit an error reaching the model. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  const send = () => sendText(input.trim());

  const transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "audio.webm");
      const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const text = (data.text || "").trim();
      if (text) { await sendText(text); }
      else { toast("I didn't catch that — please try recording again."); }
    } catch {
      toast.error("Could not transcribe the audio. Please try again.");
    } finally {
      setTranscribing(false);
    }
  };

  const startRecording = async () => {
    if (recording || transcribing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size) await transcribe(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Microphone access was blocked. Please allow mic access and try again.");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); }
  };

  const toggleAway = async () => {
    const next = !awayMode;
    setAwayMode(next);
    try {
      await api.post("/ai/away-mode", { enabled: next });
      toast.success(next ? "Away Mode on — your Capital Crew keeps executing pending missions while you're offline." : "Away Mode off.");
    } catch {
      setAwayMode(!next);
      toast.error("Could not update Away Mode.");
    }
  };

  const speak = async (text, idx) => {
    try {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (speakingIdx === idx) { setSpeakingIdx(null); return; }
      setSpeakingIdx(idx);
      const res = await api.post("/ai/speak", { text }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeakingIdx(null); URL.revokeObjectURL(url); };
      await audio.play();
    } catch {
      toast.error("Could not play the reply aloud.");
      setSpeakingIdx(null);
    }
  };

  return (
    <div className={full ? "fixed inset-0 z-50 flex flex-col bg-white p-4 sm:p-6" : "animate-fade-up"} data-testid="command-center-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold tracking-tight text-slate-900">Command Center</h1>
          <p className="text-[13px] text-slate-500">Direct your Capital Crew. The AI Controller (COO) turns your instructions into missions.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleAway} data-testid="away-mode-toggle"
            title="When on, your Capital Crew keeps executing your pending missions on a schedule while you're offline."
            className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-bold transition-colors ${awayMode ? "bg-[#080d1a] text-white" : "border border-slate-200 bg-white text-slate-500"}`}>
            <Moon size={14} className={awayMode ? "fill-white" : ""} /> Away {awayMode ? "On" : "Off"}
          </button>
          <button onClick={() => setAutoPilot((v) => !v)} data-testid="autopilot-toggle"
            title="When on, your Capital Crew executes the work and keeps moving — stopping only when it truly needs you."
            className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-bold transition-colors ${autoPilot ? "bg-[#1a5fd0] text-white" : "border border-slate-200 bg-white text-slate-500"}`}>
            <Zap size={14} className={autoPilot ? "fill-white" : ""} /> Auto-Pilot {autoPilot ? "On" : "Off"}
          </button>
          <div className="relative">
            <Building2 size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="company-selector"
              className="appearance-none rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-8 text-[13px] font-medium text-slate-700 outline-none focus:border-[#1a5fd0]">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          </div>
          <button onClick={() => setFull((v) => !v)} data-testid="chat-fullscreen" className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50">
            {full ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      </div>

      <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex-1 space-y-4 overflow-y-auto p-5" style={{ minHeight: full ? "auto" : 420, maxHeight: full ? "none" : 520 }}>
          {messages.length === 0 && (
            <div className="flex h-full min-h-[320px] flex-col items-center justify-center text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#080d1a] text-white"><Bot size={26} /></span>
              <h2 className="mt-4 font-head text-xl font-extrabold text-slate-900">What would you like your Capital Crew to do?</h2>
              <p className="mt-1 max-w-md text-[13px] text-slate-500">Type it, or tap the mic and just say it — e.g. "Build a landing page for our Net-30 program and prepare an email to existing customers."</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-[14px] leading-relaxed ${m.role === "user" ? "bg-[#1a5fd0] text-white" : "border border-slate-200 bg-slate-50 text-slate-800"}`}>
                {m.role === "controller" && <div className="mb-1 flex items-center gap-1.5 font-mono text-[9px] font-bold uppercase tracking-wider text-[#b0862f]"><Sparkles size={11} /> AI Controller · COO</div>}
                {m.role === "controller" ? <Md>{m.text}</Md> : <div className="whitespace-pre-wrap">{m.text}</div>}
                {m.assignees && m.assignees.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {m.assignees.map((a) => <span key={a} className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200">{a}</span>)}
                  </div>
                )}
                {m.deliverables && m.deliverables.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="deliverables">
                    {m.deliverables.map((d) => (
                      <div key={d.agent} className="rounded-xl border border-emerald-200 bg-white p-3" data-testid="deliverable-card">
                        <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[9px] font-bold uppercase tracking-wider text-emerald-600">
                          <span className="flex items-center gap-1"><CheckCircle2 size={11} /> {d.agent} · delivered</span>
                          {d.handoff && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-500"><ArrowRight size={9} /> {d.handoff}</span>}
                        </div>
                        <Md>{d.result}</Md>
                      </div>
                    ))}
                  </div>
                )}
                {m.mission_id && <Link to="/owner/missions" className="mt-2 inline-block text-[11px] font-semibold text-[#1a5fd0] hover:underline">View mission →</Link>}
                {m.role === "controller" && (
                  <button onClick={() => speak(m.text, i)} data-testid="speak-btn"
                    className="ml-3 mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-[#1a5fd0]">
                    {speakingIdx === i ? <Loader2 size={12} className="animate-spin" /> : <Volume2 size={12} />} {speakingIdx === i ? "Stop" : "Listen"}
                  </button>
                )}
                {m.model && <div className="mt-1 font-mono text-[9px] text-slate-400">{m.model}</div>}
              </div>
            </div>
          ))}
          {sending && <div className="flex items-center gap-2 text-[13px] text-slate-500"><Loader2 size={14} className="animate-spin text-[#1a5fd0]" /> {autoPilot ? "Auto-Pilot is executing your request…" : "AI Controller is coordinating your Capital Crew…"}</div>}
          <div ref={endRef} />
        </div>

        <div className="border-t border-slate-100 p-3">
          <div className="flex items-end gap-2">
            <div className="relative hidden shrink-0 sm:block">
              <button type="button" onClick={() => setTeamOpen((v) => !v)} data-testid="team-picker"
                className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-2.5 text-[12px] font-medium text-slate-600 hover:bg-slate-50">
                {selectedEmployees.length ? `${selectedEmployees.length} selected` : "Auto-assign"}
                <ChevronDown size={13} />
              </button>
              {teamOpen && (
                <div className="absolute bottom-full left-0 z-20 mb-2 max-h-72 w-64 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg" data-testid="team-picker-menu">
                  <button onClick={() => { setSelectedEmployees([]); setTeamOpen(false); }}
                    className="w-full rounded-lg px-2 py-1.5 text-left text-[12px] font-semibold text-slate-500 hover:bg-slate-50">Auto-assign (let the Controller decide)</button>
                  {employees.filter((e) => e.id !== "controller").map((e) => {
                    const on = selectedEmployees.includes(e.id);
                    return (
                      <button key={e.id} data-testid={`team-opt-${e.id}`}
                        onClick={() => setSelectedEmployees((s) => on ? s.filter((x) => x !== e.id) : [...s, e.id])}
                        className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] hover:bg-slate-50 ${on ? "font-semibold text-[#1a5fd0]" : "text-slate-600"}`}>
                        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? "border-[#1a5fd0] bg-[#1a5fd0] text-white" : "border-slate-300"}`}>{on && <Check size={11} />}</span>
                        {e.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button title="Attachments (coming soon)" onClick={() => toast("File attachments are coming soon.")} className="shrink-0 rounded-lg border border-slate-200 p-2.5 text-slate-400 hover:bg-slate-50"><Paperclip size={16} /></button>
            <button onClick={recording ? stopRecording : startRecording} disabled={transcribing || sending} data-testid="voice-record-btn"
              title={recording ? "Stop and send" : "Record a voice instruction"}
              className={`shrink-0 rounded-lg p-2.5 transition-colors disabled:opacity-40 ${recording ? "animate-pulse bg-red-500 text-white" : "border border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              {transcribing ? <Loader2 size={16} className="animate-spin" /> : recording ? <Square size={16} /> : <Mic size={16} />}
            </button>
            <textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={recording ? "Listening… tap the stop button when you're done" : transcribing ? "Transcribing your voice…" : "Type, or tap the mic and speak…"}
              disabled={recording || transcribing} data-testid="ceo-chat-input"
              className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2.5 text-[14px] outline-none focus:border-[#1a5fd0] disabled:bg-slate-50" />
            <button onClick={send} disabled={sending || !input.trim()} data-testid="ceo-chat-send"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#080d1a] text-white transition-opacity disabled:opacity-40">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
